import { apiClient } from '@api/apiClient';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import {
  FacilityDetails,
  serializeFacilityDetails,
  StopFacilityDetails,
  stopFacilityToFacility,
} from '@common/facilityDetails';
import { createError } from '@utils/errorUtils';
import { logError } from '@utils/errorLogging';

import { fetchAddressBookById } from './services';
import { AddressBook, AddressBookModel } from './types';

export type FacilityAddressWriteInput = {
  customerId: number;
  customerAddressId?: string | null;
  locationName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string;
  contact: string;
  phone: string;
  email: string;
  instructions: string;
  directions: string;
  description: string;
  facilityDetails: StopFacilityDetails;
};

type AddressWriteBody = AddressBook & {
  addressLine1: string;
  contactEmail: string | null;
  createdOn?: string;
};

const facilityForWrite = (
  stop: StopFacilityDetails,
  existing?: FacilityDetails | null,
): FacilityDetails =>
  serializeFacilityDetails({
    ...stopFacilityToFacility(stop),
    id: existing?.id ?? stop.facilityDetailsId ?? 0,
    version: existing?.version ?? stop.facilityDetailsVersion ?? 0,
  });

const addressBody = (
  existing: AddressBookModel | null,
  input: FacilityAddressWriteInput,
): AddressWriteBody => {
  const body: AddressWriteBody = {
    ...(existing ?? {}),
    id:
      existing == null
        ? undefined
        : (input.customerAddressId ?? existing.id ?? undefined),
    address1: input.address1,
    addressLine1: input.address1,
    addressLine2: input.address2 || null,
    name: input.locationName,
    city: input.city,
    state: input.state,
    zip: input.zip,
    countryCode: input.countryCode || existing?.countryCode || 'USA',
    contactName: input.contact || null,
    contactPhone: input.phone || null,
    contactEmail: input.email || null,
    specialInstructions: input.instructions || null,
    drivingDirections: input.directions || '',
    internalNote: input.description || null,
    customerId: input.customerId,
    description: existing?.description ?? '',
    isDefaultAddress: existing?.isDefaultAddress ?? false,
    openTimeFrom: existing?.openTimeFrom ?? '',
    openTimeTo: existing?.openTimeTo ?? '',
    customerAddressAccessorials: existing?.customerAddressAccessorials ?? [],
    appointmentType: input.facilityDetails.appointmentType,
    schedulingMethod: input.facilityDetails.schedulingMethod,
    facilityDetails: facilityForWrite(
      input.facilityDetails,
      existing?.facilityDetails,
    ),
  };
  if (existing?.createdOn) body.createdOn = existing.createdOn;
  return body;
};

const addressFromResult = (response: unknown): AddressBookModel | null => {
  if (typeof response !== 'object' || response == null) return null;
  const result = response as {
    value?: AddressBookModel;
    facilityDetails?: FacilityDetails | null;
    id?: string | null;
  };
  if (result.value != null) return result.value;
  if (result.facilityDetails != null || result.id != null) {
    return response as AddressBookModel;
  }
  return null;
};

const readSavedAddress = (response: unknown): AddressBookModel => {
  if (typeof response === 'object' && response != null) {
    const result = response as { isFailure?: boolean; errors?: unknown };
    if (result.isFailure) {
      throw result.errors ?? new Error('Failed to save address');
    }
  }
  const address = addressFromResult(response);
  if (
    address == null ||
    address.facilityDetails == null ||
    address.facilityDetails.id <= 0
  ) {
    throw new Error('Address book did not return facility details');
  }
  return address;
};

export const createFacilityAddress = async (
  input: FacilityAddressWriteInput,
): Promise<AddressBookModel> => {
  try {
    const response: unknown = await apiClient.post(
      'CustomerAddress',
      addressBody(null, input),
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.stopsAddToAddressBook,
      ]),
    );
    return readSavedAddress(response);
  } catch (error) {
    logError('createFacilityAddress', error as Error);
    throw createError(error, 'Failed to add address to address book');
  }
};

export const updateLinkedFacilityAddress = async (
  input: FacilityAddressWriteInput,
): Promise<AddressBookModel> => {
  if (input.customerAddressId == null || input.customerAddressId.length === 0) {
    throw new Error('This stop is not linked to an address book entry.');
  }
  try {
    const existing = await fetchAddressBookById(input.customerAddressId);
    const response: unknown = await apiClient.put(
      'CustomerAddress',
      addressBody(existing, input),
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.addressBookUpdate,
      ]),
    );
    return readSavedAddress(response);
  } catch (error) {
    logError('updateLinkedFacilityAddress', error as Error);
    throw createError(error, 'Failed to update address book entry');
  }
};
